import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  WeightSeriesRange,
  WeightTrackingRepository,
} from '../../application/ports/weight-tracking.repository';
import {
  InvalidDateRangeError,
  InvalidWeightInputError,
  NetworkError,
  ServerError,
  UnauthorizedError,
  WeightEntryNotFoundError,
  type WeightTrackingError,
} from '../../domain/errors';
import type {WeightEntry, WeightEntryId} from '../../domain/weight-entry';
import type {WeightEntryDtoToWeightEntryMapper} from '../mappers/weight-entry-dto-to-weight-entry.mapper';

export class RpcWeightTrackingRepository implements WeightTrackingRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly mapper: WeightEntryDtoToWeightEntryMapper,
  ) {}

  async record(weightKg: number): Promise<Result<WeightEntry, WeightTrackingError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['weight-entries'].$post({
          json: {weightKg},
        }),
      );
      return ok(this.mapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'weight');
    }
  }

  async updateEntry(
    weightEntryId: WeightEntryId,
    weightKg: number,
  ): Promise<Result<WeightEntry, WeightTrackingError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['weight-entries'][':weightEntryId'].$patch({
          param: {weightEntryId},
          json: {weightKg},
        }),
      );
      return ok(this.mapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'weight');
    }
  }

  async deleteEntry(weightEntryId: WeightEntryId): Promise<Result<void, WeightTrackingError>> {
    try {
      await parseResponse(
        this.rpcClient['weight-entries'][':weightEntryId'].$delete({
          param: {weightEntryId},
        }),
      );

      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause, 'weight');
    }
  }

  async getLatest(): Promise<Result<WeightEntry | null, WeightTrackingError>> {
    try {
      const dto = await parseResponse(this.rpcClient['weight-entries'].latest.$get());
      if (dto === null) {
        return ok(null);
      }
      return ok(this.mapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'weight');
    }
  }

  async list(cursor: string | null): Promise<Result<Page<WeightEntry>, WeightTrackingError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['weight-entries'].$get({
          query: {
            ...(cursor !== null && {cursor}),
          },
        }),
      );

      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.mapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause, 'weight');
    }
  }

  async getSeries(
    range: WeightSeriesRange,
  ): Promise<Result<readonly WeightEntry[], WeightTrackingError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['weight-entries'].series.$get({
          query: {
            from: range.from,
            to: range.to,
          },
        }),
      );

      if ('error' in dto) {
        return err(new ServerError());
      }

      return ok(dto.data.map(item => this.mapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'range');
    }
  }

  private mapError(cause: unknown, kind: 'weight' | 'range'): Result<never, WeightTrackingError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new WeightEntryNotFoundError());
      }
      if (cause.statusCode === 400) {
        return err(kind === 'range' ? new InvalidDateRangeError() : new InvalidWeightInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
